import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';
import { addError, getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';
import * as Sentry from '@sentry/react';
import type { FallbackRender } from '@sentry/react';
import React, { useCallback, useContext } from 'react';

import { DefaultFallback, FlagFallback } from './fallbacks';

export const ErrorBoundary = ({
	children,
	fallback,
	onError,
	componentName,
}: {
	children: React.ReactNode;
	fallback?: React.ReactElement | FallbackRender | 'flag' | 'unmount';
	onError?: ((error: Error, componentStack: string, eventId: string) => void) | undefined;
	componentName: string;
}) => {
	const labelStack = useContext(UFOInteractionContext)?.labelStack;

	const handleError = useCallback(
		(error: Error, componentStack: string, eventId: string) => {
			const interactionId = getActiveInteraction()?.id;
			onError?.(error, componentStack, eventId);

			if (interactionId) {
				addError(
					interactionId,
					`${componentName}.ErrorBoundary`,
					labelStack || null,
					'errorBoundaryHit',
					error.message,
					error.stack,
				);
			}
		},
		[onError, labelStack, componentName],
	);

	const getFallback = () => {
		switch (fallback) {
			case 'flag':
				return <FlagFallback />;
			case 'unmount':
				return undefined;
			default:
				return fallback ?? <DefaultFallback />;
		}
	};

	return (
		<Sentry.ErrorBoundary fallback={getFallback()} onError={handleError}>
			{children}
		</Sentry.ErrorBoundary>
	);
};
